import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { products } from "@/mocks/products";
import { suppliers } from "@/mocks/suppliers";

type OrderType = "sample" | "bulk" | null;

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const product = products.find((p) => p.id === id);
  const supplier = product ? suppliers.find((s) => s.id === product.supplierId) : null;

  const [orderType, setOrderType] = useState<OrderType>(null);
  const [quantity, setQuantity] = useState(1);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "supplier" | "reviews">("details");
  const [inquiryMsg, setInquiryMsg] = useState("");
  const [inquirySent, setInquirySent] = useState(false);

  if (!product) {
    return (
      <div className="min-h-screen bg-amber-50">
        <Navbar />
        <div className="pt-40 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
            <i className="ri-search-line text-3xl text-amber-400"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-500 text-sm mb-6">This product may have been removed or the link is incorrect.</p>
          <button onClick={() => navigate("/products")} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-lg cursor-pointer whitespace-nowrap">
            Browse All Products
          </button>
        </div>
      </div>
    );
  }

  const isSample = orderType === "sample";
  const isBulk = orderType === "bulk";
  const unitPrice = isSample ? product.price.max : product.price.min;
  const minQty = isSample ? 1 : product.moq;
  const totalPrice = (unitPrice * quantity).toFixed(2);

  const handleOrder = () => {
    if (!orderType) return;
    setOrderSubmitted(true);
  };

  const handleInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    setInquirySent(true);
  };

  const relatedProducts = products.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 3);

  const mockReviews = [
    { name: "James K.", company: "TechRetail UK", country: "🇬🇧", rating: 5, text: "Excellent quality, arrived on time. The supplier was very responsive and professional throughout.", date: "Mar 2026" },
    { name: "Fatima Al-Rashid", company: "Gulf Trading Co.", country: "🇸🇦", rating: 5, text: "We ordered 500 units for our store. Quality exceeded expectations. Will definitely reorder.", date: "Feb 2026" },
    { name: "Chen Wei", company: "Asia Dropship Hub", country: "🇨🇳", rating: 4, text: "Good product, dropshipping setup was smooth. Minor packaging issue on 2 units but supplier resolved quickly.", date: "Jan 2026" },
  ];

  return (
    <div className="min-h-screen bg-amber-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 bg-white border-b border-amber-100">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-amber-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/products" className="hover:text-amber-600 cursor-pointer">Products</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-400">{product.category}</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800 font-medium truncate max-w-xs">{product.name}</span>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: Image + Tabs */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Image */}
            <div className="bg-white rounded-2xl overflow-hidden border border-amber-100">
              <div className="relative h-72 md:h-96">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                  {product.verified && (
                    <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-shield-check-line"></i> Verified Supplier
                    </span>
                  )}
                  {product.dropshipping && (
                    <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-truck-line"></i> Dropshipping Available
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-amber-100 overflow-hidden">
              <div className="flex border-b border-amber-100">
                {(["details", "supplier", "reviews"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-3 text-sm font-semibold capitalize transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab ? "text-amber-600 border-b-2 border-amber-500 bg-amber-50" : "text-gray-500 hover:text-amber-500"}`}
                  >
                    {tab === "details" ? "Product Details" : tab === "supplier" ? "Supplier Info" : `Reviews (${mockReviews.length})`}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{product.name}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                      {[
                        { label: "Category", value: product.category, icon: "ri-price-tag-3-line" },
                        { label: "Country", value: `${product.country} · ${product.city}`, icon: "ri-map-pin-line" },
                        { label: "Min. Order", value: `${product.moq} pcs`, icon: "ri-box-3-line" },
                        { label: "Sample Price", value: `$${product.price.max}/unit`, icon: "ri-test-tube-line" },
                        { label: "Bulk Price", value: `$${product.price.min}/unit`, icon: "ri-stack-line" },
                        { label: "Dropshipping", value: product.dropshipping ? "Available" : "Not available", icon: "ri-truck-line" },
                      ].map((item) => (
                        <div key={item.label} className="bg-amber-50 rounded-xl p-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <i className={`${item.icon} text-amber-500 text-sm`}></i>
                            <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                          </div>
                          <span className={`text-sm font-bold ${item.label === "Dropshipping" && product.dropshipping ? "text-emerald-600" : "text-gray-900"}`}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-amber-100 pt-4">
                      <h3 className="font-bold text-gray-900 mb-2 text-sm">Product Description</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        This premium {product.name.toLowerCase()} is manufactured by {product.supplierName} in {product.city}, {product.country}. 
                        Suitable for both retail and wholesale buyers. Available for sample orders (1 unit minimum) and bulk orders ({product.moq}+ units) with competitive pricing. 
                        {product.dropshipping && " Dropshipping is fully supported — we ship directly to your customers worldwide."}
                        {" "}All products come with quality inspection and are backed by our verified supplier guarantee.
                      </p>
                    </div>
                    <div className="border-t border-amber-100 pt-4 mt-4">
                      <h3 className="font-bold text-gray-900 mb-3 text-sm">Pricing Tiers</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-amber-50">
                              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 rounded-l-lg">Order Type</th>
                              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Quantity</th>
                              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Unit Price</th>
                              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 rounded-r-lg">Lead Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-amber-50">
                              <td className="px-3 py-2.5 font-semibold text-amber-700">Sample Order</td>
                              <td className="px-3 py-2.5 text-gray-600">1 – {product.moq - 1} pcs</td>
                              <td className="px-3 py-2.5 font-bold text-gray-900">${product.price.max}</td>
                              <td className="px-3 py-2.5 text-gray-600">3–7 days</td>
                            </tr>
                            <tr className="border-b border-amber-50">
                              <td className="px-3 py-2.5 font-semibold text-emerald-700">Bulk Order</td>
                              <td className="px-3 py-2.5 text-gray-600">{product.moq}+ pcs</td>
                              <td className="px-3 py-2.5 font-bold text-gray-900">${product.price.min}</td>
                              <td className="px-3 py-2.5 text-gray-600">7–21 days</td>
                            </tr>
                            {product.dropshipping && (
                              <tr>
                                <td className="px-3 py-2.5 font-semibold text-blue-700">Dropship</td>
                                <td className="px-3 py-2.5 text-gray-600">1 pc (per order)</td>
                                <td className="px-3 py-2.5 font-bold text-gray-900">${product.price.max}</td>
                                <td className="px-3 py-2.5 text-gray-600">2–5 days</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Supplier Tab */}
                {activeTab === "supplier" && supplier && (
                  <div>
                    <div className="flex items-start gap-4 mb-6">
                      <img src={supplier.logo} alt={supplier.name} className="w-16 h-16 rounded-xl object-cover border border-amber-100 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-900 text-base">{supplier.name}</h3>
                          {supplier.verified && <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1"><i className="ri-shield-check-line"></i> Verified</span>}
                          {supplier.dropshipping && <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1"><i className="ri-truck-line"></i> Dropship</span>}
                        </div>
                        <p className="text-gray-500 text-sm mt-1">{supplier.city}, {supplier.country} · Est. {supplier.established}</p>
                        <p className="text-gray-600 text-sm mt-2">{supplier.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                      {[
                        { label: "Response Rate", value: supplier.responseRate, icon: "ri-message-3-line" },
                        { label: "On-Time Delivery", value: supplier.onTimeDelivery, icon: "ri-truck-line" },
                        { label: "Products", value: supplier.productCount.toLocaleString(), icon: "ri-box-3-line" },
                        { label: "Employees", value: supplier.employees, icon: "ri-team-line" },
                      ].map((s) => (
                        <div key={s.label} className="bg-amber-50 rounded-xl p-3 text-center">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-1">
                            <i className={`${s.icon} text-amber-600 text-sm`}></i>
                          </div>
                          <div className="font-bold text-gray-900 text-sm">{s.value}</div>
                          <div className="text-xs text-gray-500">{s.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mb-4">
                      <h4 className="font-bold text-gray-900 text-sm mb-2">Product Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        {supplier.categories.map((c) => <span key={c} className="bg-amber-100 text-amber-700 text-xs px-3 py-1 rounded-full font-medium">{c}</span>)}
                      </div>
                    </div>
                    {/* Quick Inquiry */}
                    {inquirySent ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                        <i className="ri-checkbox-circle-fill text-2xl text-emerald-500 mb-1 block"></i>
                        <p className="text-emerald-700 font-semibold text-sm">Inquiry sent! The supplier will respond within 24 hours.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleInquiry} className="border border-amber-100 rounded-xl p-4 bg-amber-50">
                        <h4 className="font-bold text-gray-900 text-sm mb-3">Send Quick Inquiry</h4>
                        <textarea value={inquiryMsg} onChange={(e) => setInquiryMsg(e.target.value)} rows={3} maxLength={500} placeholder="Ask about pricing, customization, lead times..." className="w-full border border-amber-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white resize-none mb-3" required />
                        <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap">
                          Send Inquiry
                        </button>
                      </form>
                    )}
                  </div>
                )}

                {/* Reviews Tab */}
                {activeTab === "reviews" && (
                  <div>
                    <div className="flex items-center gap-4 mb-6 p-4 bg-amber-50 rounded-xl">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-amber-600" style={{ fontFamily: "'Playfair Display', serif" }}>{product.rating}</div>
                        <div className="flex items-center gap-0.5 justify-center mt-1">
                          {[...Array(5)].map((_, i) => <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{product.reviews} reviews</div>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const pct = star === 5 ? 72 : star === 4 ? 20 : star === 3 ? 5 : star === 2 ? 2 : 1;
                          return (
                            <div key={star} className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-3">{star}</span>
                              <i className="ri-star-fill text-amber-400 text-xs"></i>
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }}></div>
                              </div>
                              <span className="text-xs text-gray-400 w-6">{pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="space-y-4">
                      {mockReviews.map((r, i) => (
                        <div key={i} className="border border-amber-100 rounded-xl p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full text-sm font-bold text-amber-700">{r.name[0]}</div>
                              <div>
                                <div className="font-semibold text-gray-900 text-sm">{r.name} {r.country}</div>
                                <div className="text-xs text-gray-400">{r.company}</div>
                              </div>
                            </div>
                            <span className="text-xs text-gray-400">{r.date}</span>
                          </div>
                          <div className="flex items-center gap-0.5 mb-2">
                            {[...Array(5)].map((_, j) => <i key={j} className={`ri-star-fill text-xs ${j < r.rating ? "text-amber-400" : "text-gray-200"}`}></i>)}
                          </div>
                          <p className="text-gray-600 text-sm">{r.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Order Panel */}
          <div className="space-y-5">
            {/* Product Title Card */}
            <div className="bg-white rounded-2xl p-5 border border-amber-100">
              <h1 className="font-bold text-gray-900 text-lg mb-2 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{product.name}</h1>
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>)}
                <span className="text-xs text-gray-400 ml-1">{product.rating} ({product.reviews} reviews)</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <i className="ri-map-pin-line text-amber-500"></i> {product.city}, {product.country}
                <span className="mx-1">·</span>
                <i className="ri-building-4-line text-amber-500"></i> {product.supplierName}
              </div>
              {product.dropshipping && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-700 font-semibold">
                  <i className="ri-truck-line"></i> Dropshipping Available — Ship directly to your customers
                </div>
              )}
            </div>

            {/* Order Type Selection */}
            {!orderSubmitted ? (
              <div className="bg-white rounded-2xl p-5 border border-amber-100">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Place Your Order</h3>

                {/* Order Type Buttons */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button
                    onClick={() => { setOrderType("sample"); setQuantity(1); }}
                    className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${orderType === "sample" ? "border-amber-500 bg-amber-50" : "border-gray-100 hover:border-amber-200"}`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg mb-2">
                      <i className="ri-test-tube-line text-amber-600"></i>
                    </div>
                    <div className="font-bold text-gray-900 text-sm">Sample Order</div>
                    <div className="text-amber-600 font-bold text-base">${product.price.max}<span className="text-xs text-gray-400 font-normal">/unit</span></div>
                    <div className="text-xs text-gray-400">Min. 1 unit</div>
                  </button>
                  <button
                    onClick={() => { setOrderType("bulk"); setQuantity(product.moq); }}
                    className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${orderType === "bulk" ? "border-amber-500 bg-amber-50" : "border-gray-100 hover:border-amber-200"}`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg mb-2">
                      <i className="ri-stack-line text-emerald-600"></i>
                    </div>
                    <div className="font-bold text-gray-900 text-sm">Bulk Order</div>
                    <div className="text-emerald-600 font-bold text-base">${product.price.min}<span className="text-xs text-gray-400 font-normal">/unit</span></div>
                    <div className="text-xs text-gray-400">Min. {product.moq} units</div>
                  </button>
                </div>

                {orderType && (
                  <>
                    {/* Quantity */}
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Quantity (units)</label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setQuantity((q) => Math.max(minQty, q - (isBulk ? product.moq : 1)))}
                          className="w-9 h-9 flex items-center justify-center border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-50 cursor-pointer font-bold"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <input
                          type="number"
                          value={quantity}
                          min={minQty}
                          onChange={(e) => setQuantity(Math.max(minQty, Number(e.target.value)))}
                          className="flex-1 border border-amber-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:border-amber-400"
                        />
                        <button
                          onClick={() => setQuantity((q) => q + (isBulk ? product.moq : 1))}
                          className="w-9 h-9 flex items-center justify-center border border-amber-200 rounded-lg text-amber-600 hover:bg-amber-50 cursor-pointer font-bold"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {isSample ? "Sample orders: 1 unit minimum" : `Bulk orders: ${product.moq} units minimum`}
                      </p>
                    </div>

                    {/* Price Summary */}
                    <div className="bg-amber-50 rounded-xl p-4 mb-4 border border-amber-100">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Unit price</span>
                        <span className="font-semibold text-gray-900">${unitPrice}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Quantity</span>
                        <span className="font-semibold text-gray-900">{quantity} units</span>
                      </div>
                      <div className="border-t border-amber-200 pt-2 mt-2 flex justify-between">
                        <span className="font-bold text-gray-900">Estimated Total</span>
                        <span className="font-bold text-amber-600 text-lg">${totalPrice}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">* Final price confirmed by supplier. Shipping not included.</p>
                    </div>

                    <button
                      onClick={handleOrder}
                      className={`w-full font-bold py-3 rounded-xl text-white text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${isSample ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"}`}
                    >
                      <i className={isSample ? "ri-test-tube-line" : "ri-stack-line"}></i>
                      {isSample ? "Request Sample Order" : "Place Bulk Order"}
                    </button>
                  </>
                )}

                {!orderType && (
                  <p className="text-center text-gray-400 text-xs">Select an order type above to continue</p>
                )}

                <div className="mt-4 pt-4 border-t border-amber-100 flex items-center justify-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><i className="ri-shield-check-line text-amber-500"></i> Secure</span>
                  <span className="flex items-center gap-1"><i className="ri-customer-service-line text-amber-500"></i> 24h Support</span>
                  <span className="flex items-center gap-1"><i className="ri-refund-2-line text-amber-500"></i> Buyer Protection</span>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-6 border border-amber-100 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-3xl text-amber-500"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Order Request Sent!</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Your {orderType} order request for <strong>{quantity} units</strong> of <strong>{product.name}</strong> has been sent to {product.supplierName}.
                </p>
                <div className="bg-amber-50 rounded-xl p-3 text-left space-y-1.5 mb-4 text-xs text-gray-600">
                  <div className="flex items-center gap-2"><i className="ri-mail-check-line text-amber-500"></i> Confirmation sent to your email</div>
                  <div className="flex items-center gap-2"><i className="ri-time-line text-amber-500"></i> Supplier responds within 24 hours</div>
                  <div className="flex items-center gap-2"><i className="ri-shield-check-line text-amber-500"></i> Buyer protection applies</div>
                </div>
                <button onClick={() => { setOrderSubmitted(false); setOrderType(null); }} className="text-amber-600 text-sm font-semibold hover:underline cursor-pointer">
                  Place another order
                </button>
              </div>
            )}

            {/* Supplier Quick Info */}
            {supplier && (
              <div className="bg-white rounded-2xl p-5 border border-amber-100">
                <h3 className="font-bold text-gray-900 text-sm mb-3">About the Supplier</h3>
                <div className="flex items-center gap-3 mb-3">
                  <img src={supplier.logo} alt={supplier.name} className="w-10 h-10 rounded-lg object-cover border border-amber-100 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{supplier.name}</div>
                    <div className="text-xs text-gray-400">{supplier.city}, {supplier.country}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <div className="font-bold text-amber-600">{supplier.responseRate}</div>
                    <div className="text-gray-400">Response Rate</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <div className="font-bold text-amber-600">{supplier.onTimeDelivery}</div>
                    <div className="text-gray-400">On-Time Delivery</div>
                  </div>
                </div>
                <button onClick={() => setActiveTab("supplier")} className="w-full border border-amber-300 text-amber-600 hover:bg-amber-50 text-sm font-semibold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                  View Full Supplier Profile
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedProducts.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/products/${rp.id}`}
                  className="bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 border border-amber-100 block"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={rp.image} alt={rp.name} className="w-full h-full object-cover object-top" />
                    {rp.dropshipping && <span className="absolute top-2 left-2 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1"><i className="ri-truck-line"></i> Dropship</span>}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-amber-600 font-medium mb-1">{rp.supplierName}</p>
                    <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">{rp.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900 text-sm">${rp.price.min}–${rp.price.max}</span>
                      <span className="text-xs text-gray-400">MOQ: {rp.moq}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
